import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { CheckCircle2, ChevronDown, ChevronRight, Copy, Loader2, Plus, RotateCcw, Trash2, Webhook as WebhookIcon, XCircle } from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ChatIntegrations } from "@/components/ChatIntegrations";

const ALL_EVENTS = [
  "deployment.created",
  "deployment.ready",
  "deployment.failed",
  "deployment.cancelled",
  "deployment.skipped",
  "deployment.rolled_back",
  "deployment.promoted",
  "release.started",
  "release.completed",
  "release.aborted",
  "domain.verified",
  "domain.cert_issued",
] as const;
type EventName = typeof ALL_EVENTS[number];

interface Webhook {
  id: string;
  url: string;
  events: EventName[];
  active: boolean;
  createdAt: string;
}

interface Delivery {
  id: string;
  event: EventName;
  status: "pending" | "success" | "failed" | "cancelled";
  attempts: number;
  lastStatusCode: number | null;
  lastResponseSnippet: string | null;
  nextAttemptAt: string | null;
  createdAt: string;
  completedAt: string | null;
}

/**
 * T127 — webhook CRUD + delivery log with replay button.
 */
export function ProjectWebhooks() {
  const { org, project } = useParams<{ org: string; project: string }>();
  const qc = useQueryClient();
  const [newSecret, setNewSecret] = useState<{ id: string; secret: string } | null>(null);
  const [openLogId, setOpenLogId] = useState<string | null>(null);

  const webhooks = useQuery({
    queryKey: ["webhooks", org, project],
    queryFn: () => api.get<{ webhooks: Webhook[] }>(`/orgs/${org}/projects/${project}/webhooks`).then((r) => r.webhooks),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/orgs/${org}/projects/${project}/webhooks/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["webhooks", org, project] }),
  });
  const toggleActive = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      api.patch(`/orgs/${org}/projects/${project}/webhooks/${id}`, { active }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["webhooks", org, project] }),
  });

  return (
    <div className="space-y-5">
      <Section title="Outgoing webhooks" subtitle="Signed POSTs on deployment + domain events" iconLeft={<WebhookIcon className="h-3.5 w-3.5 text-muted-foreground" />}>
        {webhooks.isLoading && <Empty>Loading…</Empty>}
        {!webhooks.isLoading && (webhooks.data?.length ?? 0) === 0 && (
          <Empty>No webhooks yet. Add one below to start receiving signed event POSTs.</Empty>
        )}
        {webhooks.data?.map((wh) => (
          <div key={wh.id} className="border-b border-border-muted last:border-b-0">
            <div className="of-row gap-3 items-start">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-mono text-sm font-semibold truncate">{wh.url}</span>
                  {!wh.active && <span className="text-2xs uppercase tracking-wider bg-subtle text-subtle-foreground border border-border-muted rounded px-1.5 py-0.5">inactive</span>}
                </div>
                <div className="flex flex-wrap gap-1 mt-1">
                  {wh.events.map((e) => (
                    <span key={e} className="text-2xs uppercase tracking-wider font-mono bg-subtle border border-border-muted rounded px-1.5 py-0.5">
                      {e}
                    </span>
                  ))}
                </div>
                {newSecret?.id === wh.id && (
                  <SecretReveal secret={newSecret.secret} onDismiss={() => setNewSecret(null)} />
                )}
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setOpenLogId(openLogId === wh.id ? null : wh.id)}
                title="Toggle deliveries"
              >
                {openLogId === wh.id ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
              </Button>
              <label className="text-xs flex items-center gap-1 cursor-pointer">
                <input
                  type="checkbox"
                  checked={wh.active}
                  onChange={(e) => toggleActive.mutate({ id: wh.id, active: e.target.checked })}
                />
                active
              </label>
              <Button
                variant="ghost"
                size="icon"
                className="text-danger"
                disabled={remove.isPending}
                onClick={() => { if (confirm("Delete this webhook?")) remove.mutate(wh.id); }}
              >
                {remove.isPending && remove.variables === wh.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
              </Button>
            </div>
            {openLogId === wh.id && <DeliveriesLog orgSlug={org!} projectSlug={project!} webhookId={wh.id} />}
          </div>
        ))}
      </Section>

      <AddWebhookForm
        orgSlug={org!}
        projectSlug={project!}
        onCreated={(id, secret) => {
          setNewSecret({ id, secret });
          qc.invalidateQueries({ queryKey: ["webhooks", org, project] });
        }}
      />

      <ChatIntegrations orgSlug={org!} projectSlug={project!} />
    </div>
  );
}

function DeliveriesLog({ orgSlug, projectSlug, webhookId }: { orgSlug: string; projectSlug: string; webhookId: string }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["webhook-deliveries", webhookId],
    queryFn: () =>
      api
        .get<{ deliveries: Delivery[] }>(`/orgs/${orgSlug}/projects/${projectSlug}/webhooks/${webhookId}/deliveries`)
        .then((r) => r.deliveries),
    refetchInterval: 10_000,
  });

  const replay = useMutation({
    mutationFn: (deliveryId: string) =>
      api.post(`/orgs/${orgSlug}/projects/${projectSlug}/webhooks/${webhookId}/deliveries/${deliveryId}/replay`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["webhook-deliveries", webhookId] }),
  });

  return (
    <div className="bg-subtle/50 border-t border-border-muted px-4 py-2.5">
      {q.isLoading && <div className="text-2xs text-muted-foreground">Loading deliveries…</div>}
      {!q.isLoading && (q.data?.length ?? 0) === 0 && (
        <div className="text-2xs text-muted-foreground">No deliveries yet — fire a deployment event to test.</div>
      )}
      <div className="space-y-1">
        {q.data?.map((d) => (
          <div key={d.id} className="flex flex-wrap items-center gap-2 text-2xs font-mono">
            <StatusIcon status={d.status} />
            <span className="w-32 sm:w-44 truncate">{d.event}</span>
            <span className="text-subtle-foreground w-12 tabular-nums">{d.attempts}×</span>
            <span className="text-subtle-foreground w-12 tabular-nums">{d.lastStatusCode ?? "—"}</span>
            <span className="flex-1 min-w-0 truncate text-subtle-foreground">{d.lastResponseSnippet ?? ""}</span>
            <span className="text-subtle-foreground whitespace-nowrap">{relative(d.createdAt)}</span>
            <Button
              variant="ghost"
              size="icon"
              title="Replay"
              onClick={() => replay.mutate(d.id)}
              disabled={replay.isPending}
              className="h-6 w-6"
            >
              {replay.isPending && replay.variables === d.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <RotateCcw className="h-3 w-3" />}
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}

function AddWebhookForm({ orgSlug, projectSlug, onCreated }: {
  orgSlug: string;
  projectSlug: string;
  onCreated: (id: string, secret: string) => void;
}) {
  const [url, setUrl] = useState("");
  const [events, setEvents] = useState<EventName[]>(["deployment.ready", "deployment.failed"]);
  const create = useMutation({
    mutationFn: () =>
      api.post<{ webhook: { id: string }; secret: string }>(`/orgs/${orgSlug}/projects/${projectSlug}/webhooks`, {
        url, events, active: true,
      }),
    onSuccess: (r) => {
      onCreated(r.webhook.id, r.secret);
      setUrl("");
    },
  });
  return (
    <details className="of-card">
      <summary className="of-card-header cursor-pointer list-none">
        <h3 className="text-sm font-semibold inline-flex items-center gap-1.5">
          <Plus className="h-3.5 w-3.5" /> Add webhook
        </h3>
      </summary>
      <div className="px-4 py-3 space-y-2">
        <Label htmlFor="wh-url" className="text-xs">Endpoint URL</Label>
        <Input id="wh-url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://example.com/openfront-hook" className="font-mono text-sm" />
        <div className="text-xs text-muted-foreground pt-1">Events</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
          {ALL_EVENTS.map((e) => (
            <label key={e} className="text-xs flex items-center gap-1.5 cursor-pointer">
              <input
                type="checkbox"
                checked={events.includes(e)}
                onChange={(ev) => setEvents((cur) => ev.target.checked ? Array.from(new Set([...cur, e])) : cur.filter((x) => x !== e))}
              />
              <span className="font-mono">{e}</span>
            </label>
          ))}
        </div>
        {create.error instanceof ApiError && <div className="text-xs text-danger">{create.error.message}</div>}
        <div className="flex justify-end">
          <Button
            variant="primary"
            size="sm"
            onClick={() => create.mutate()}
            disabled={!url || events.length === 0 || create.isPending}
          >
            {create.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
            Create webhook
          </Button>
        </div>
      </div>
    </details>
  );
}

function SecretReveal({ secret, onDismiss }: { secret: string; onDismiss: () => void }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="mt-2 rounded-md border border-success/30 bg-success-subtle/50 p-3 space-y-2">
      <div className="text-2xs font-semibold">Signing secret (shown ONCE — copy now)</div>
      <div className="flex flex-wrap items-center gap-2">
        <input
          readOnly
          value={secret}
          onClick={(e) => (e.target as HTMLInputElement).select()}
          className="flex-1 min-w-0 h-8 px-2 rounded-md border border-border bg-background text-xs font-mono"
        />
        <Button
          size="sm"
          variant="default"
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(secret);
            } catch {
              const ta = document.createElement("textarea");
              ta.value = secret;
              document.body.appendChild(ta);
              ta.select();
              document.execCommand("copy");
              document.body.removeChild(ta);
            }
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          }}
        >
          {copied ? <CheckCircle2 className="h-3.5 w-3.5 text-success" /> : <Copy className="h-3.5 w-3.5" />}
          {copied ? "Copied" : "Copy"}
        </Button>
        <Button size="sm" variant="ghost" onClick={onDismiss}>Dismiss</Button>
      </div>
      <div className="text-2xs text-muted-foreground">
        Verify: <span className="font-mono">sha256(rawBody, secret)</span> in <span className="font-mono">X-Openfront-Signature-256</span>.
      </div>
    </div>
  );
}

function StatusIcon({ status }: { status: Delivery["status"] }) {
  if (status === "success") return <CheckCircle2 className="h-3 w-3 text-success" />;
  if (status === "failed") return <XCircle className="h-3 w-3 text-danger" />;
  if (status === "cancelled") return <XCircle className="h-3 w-3 text-subtle-foreground" />;
  return <Loader2 className="h-3 w-3 text-attention" />;
}

function Section({ title, subtitle, iconLeft, children }: { title: string; subtitle?: string; iconLeft?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="of-card">
      <div className="of-card-header">
        <h3 className="text-sm font-semibold inline-flex items-center gap-1.5">
          {iconLeft}{title}
        </h3>
        {subtitle && <span className="hidden sm:inline text-2xs text-subtle-foreground">{subtitle}</span>}
      </div>
      {children}
    </div>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <div className="px-4 py-10 text-center text-sm text-muted-foreground">{children}</div>;
}

function relative(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}
